import React from 'react'
import { Mention, MentionsInput } from 'react-mentions'
// Removed invalid import for SuggestionDataItem
import { AvatarText } from '@/components/molecules/text/AvatarText'
import { elevateColor } from '@/style/palette'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import { darken, useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import Color from 'color'
import { RenderReaction } from './ChatMessageText'
import { REACTIONS } from './REACTIONS'

const MAX_CHAT_HEIGHT = 100

export const ChatMentionsContainer = ({
  values,
  setFieldValue,
  submitForm,
  onCancel,
  onEditPrevious,
  handleTyping,
  inputRef,
  validUniqueAts
}: {
  values: { message: string }
  setFieldValue: (field: string, value: any) => void
  submitForm: () => void
  onCancel?: () => void
  onEditPrevious?: () => void
  handleTyping: () => void
  inputRef: React.RefObject<HTMLInputElement>
  validUniqueAts: Array<{ id: string; display: string; subtitle?: string }>
}) => {
  const theme = useTheme()

  return (
    <Box
      sx={theme => ({
        width: '100%',
        wordBreak: 'break-word',
        fontSize: theme.typography.subtitle2.fontSize,
        '& .mentions__control ': {
          padding: theme.spacing(1),
          maxHeight: MAX_CHAT_HEIGHT,
          backgroundColor: elevateColor(theme.palette.background.paper, 4),
          overflow: 'hidden',
          borderRadius: 4
        },
        '& .mentions__highlighter ': {
          padding: theme.spacing(1),
          paddingRight: { xs: '80px', md: '30px' },
          maxHeight: MAX_CHAT_HEIGHT
        },
        '& .mentions__input': {
          width: '100%',
          color: theme.palette.text.primary,
          padding: theme.spacing(2),
          maxHeight: MAX_CHAT_HEIGHT,
          borderColor: theme.palette.divider,
          borderWidth: 1,
          borderRadius: 4
        },
        '& .mentions__input:focus': {
          outline: 'none !important',
          borderColor: theme.palette.primary.main
        },
        '.mentions__suggestions': {
          backgroundColor: `${darken(
            theme?.palette.background.paper,
            0.8
          )} !important`,
          paddingY: theme.spacing(2),
          borderRadius: 1,
          maxHeight: 300,
          overflowY: 'auto'
        },
        '.mentions__suggestions__item': {
          minWidth: 250,
          paddingY: theme.spacing(2),
          paddingX: theme.spacing(4),
          backgroundColor: elevateColor(theme.palette.background.paper, 4)
        },
        '.mentions__suggestions__item--focused': {
          backgroundColor: elevateColor(theme.palette.background.paper, 6)
        }
      })}
    >
      <MentionsInput
        className='mentions'
        inputRef={inputRef}
        value={values.message}
        onChange={evt => {
          const val = evt.target.value
          setFieldValue('message', val.trimStart())
        }}
        onKeyDown={evt => {
          if (evt.key === 'Enter' && !evt.shiftKey) submitForm()
          if (evt.key === 'Escape') onCancel?.()
          if (evt.key === 'ArrowUp' && !values.message) {
            onEditPrevious?.()
          }
          handleTyping()
        }}
        allowSuggestionsAboveCursor
        forceSuggestionsAboveCursor
      >
        <Mention
          trigger='@'
          data={validUniqueAts}
          markup={`[@__display__](__id__)`}
          displayTransform={(val, display) => `@${display}`}
          renderSuggestion={(val: {
            id: string | number
            display?: string
            subtitle?: string
          }) =>
            Number(val.id) > 1 ? (
              <AvatarText src={val.subtitle} size='small'>
                <Typography>{val.display}</Typography>
              </AvatarText>
            ) : (
              <Stack
                direction='row'
                spacing={1}
                alignItems='center'
                justifyContent='space-between'
              >
                <Typography variant='body0'>@{val.display}</Typography>
                <Typography variant='subtitle2' color='text.secondary'>
                  {val.subtitle}
                </Typography>
              </Stack>
            )
          }
          style={{
            backgroundColor: Color(theme.palette.primary.main)
              .fade(0.75)
              .rgb()
              .string()
          }}
        />
        <Mention
          trigger=':'
          data={REACTIONS}
          markup={`[:__id__:](__display__)`}
          displayTransform={val => `:${val}:`}
          renderSuggestion={(val: any) => (
            <Stack direction='row' spacing={3} alignItems='center'>
              <RenderReaction id={val.id} />{' '}
              <Typography variant='subtitle2'>:{val.id}:</Typography>
            </Stack>
          )}
          style={{
            backgroundColor: Color(theme.palette.primary.main)
              .fade(0.75)
              .rgb()
              .string()
          }}
        />
      </MentionsInput>
    </Box>
  )
}
